import { useEffect, useMemo, useRef, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import {
  useIsFetching,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { Footer } from '@/components/Footer'
import { Header } from '@/components/Header'
import { SnowflakeBackground } from '@/components/SnowflakeBackground'
import { StationGrid } from '@/components/StationGrid'
import { StatsPanel } from '@/components/StatsPanel'
import { TickerTape } from '@/components/TickerTape'
import { toast } from 'sonner'
import {
  addUserWebcam,
  getSavedWebcams,
  removeUserWebcam,
} from '@/data/webcams'
import { WebcamSection } from '@/components/WebcamSection'
import { useAllWebcams } from '@/hooks/useAllWebcams'
import { useUserSavedWebcams } from '@/hooks/useUserSavedWebcams'
import { useUserLocations } from '@/hooks/useUserLocations'
import { MobileBottomNav } from '@/components/MobileBottomNav'
import { SnowDepthMapWrapper } from '@/components/SnowDepthMapWrapper'
import { fetchUserLocations } from '@/data/locations'
import { DashboardSkeleton } from '@/components/DashboardSkeleton'

type TabType = 'locations' | 'map' | 'radar'

export const Route = createFileRoute('/_authenticated/dashboard')({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: ['user-locations'],
      queryFn: () => fetchUserLocations(),
      staleTime: 5 * 60 * 1000,
    }),
  pendingComponent: DashboardSkeleton,
  pendingMinMs: 1000,
  component: RouteComponent,
})

function RouteComponent() {
  const queryClient = useQueryClient()
  const userWebcams = useUserSavedWebcams()
  useUserLocations()

  // Track refresh state
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date())

  // Mobile tab navigation state
  const [activeTab, setActiveTab] = useState<TabType>('locations')

  const [refreshStartTime, setRefreshStartTime] = useState<number | null>(null)
  const isFetching = useIsFetching()
  const { data: allWebcams } = useAllWebcams()

  const [newLocationId, setNewLocationId] = useState<string | null>(null)

  // Watch for when all queries finish fetching after a refresh
  useEffect(() => {
    if (isRefreshing && isFetching === 0 && refreshStartTime !== null) {
      const elapsed = Date.now() - refreshStartTime
      const minDelay = 1000
      const remainingDelay = Math.max(0, minDelay - elapsed)

      setTimeout(() => {
        setIsRefreshing(false)
        setLastRefresh(new Date())
        setRefreshStartTime(null)
        toast.success('Data refreshed')
      }, remainingDelay)
    }
  }, [isFetching, isRefreshing, refreshStartTime])

  const addWebcamMutation = useMutation({
    mutationFn: (webcamId: string) => addUserWebcam(webcamId),
    onSuccess: async (result: { error?: boolean; message?: string }) => {
      if (result.error) {
        toast.error('Error', {
          description: result.message,
        })
      } else {
        await queryClient.invalidateQueries({ queryKey: ['user-webcams'] })
        toast.success('Webcam added successfully')
      }
    },
  })

  const removeWebcamMutation = useMutation({
    mutationFn: (webcamId: string) => removeUserWebcam(webcamId),
    onSuccess: async (result: { error?: boolean; message?: string }) => {
      if (result.error) {
        toast.error('Error', {
          description: result.message,
        })
      } else {
        await queryClient.invalidateQueries({ queryKey: ['user-webcams'] })
        toast.success('Webcam removed successfully')
      }
    },
  })

  const searchTriggerRef = useRef<HTMLButtonElement>(null)

  const onAddLocationSuccess = (id: string) => {
    setNewLocationId(id)
    setTimeout(() => setNewLocationId(null), 2000)
  }

  const handleAddStationClick = () => {
    // Scroll to top where the search input is
    window.scrollTo({ top: 0, behavior: 'smooth' })
    // Small delay to let scroll start before opening popover
    setTimeout(() => {
      searchTriggerRef.current?.click()
    }, 100)
  }

  const handleAddWebcam = (webcamId: string) => {
    addWebcamMutation.mutate(webcamId)
  }

  const handleRemoveWebcam = (webcamId: string) => {
    removeWebcamMutation.mutate(webcamId)
  }

  // Trigger the station search dialog (now includes custom location functionality)
  const handleAddLocationClick = () => {
    // Scroll to top where the search input is
    window.scrollTo({ top: 0, behavior: 'smooth' })
    // Small delay to let scroll start before opening popover
    setTimeout(() => {
      searchTriggerRef.current?.click()
    }, 100)
  }

  const handleRefresh = async () => {
    setIsRefreshing(true)
    setRefreshStartTime(Date.now())

    // Invalidate all queries to refresh data
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['user-locations'] }),
      queryClient.invalidateQueries({ queryKey: ['standard-locations'] }),
      queryClient.invalidateQueries({ queryKey: ['top-gainers'] }),
      queryClient.invalidateQueries({ queryKey: ['top-forecasted-gainers'] }),
      queryClient.invalidateQueries({ queryKey: ['textual-forecast'] }),
      queryClient.invalidateQueries({ queryKey: ['user-webcams'] }),
      queryClient.invalidateQueries({ queryKey: ['all-webcams'] }),
      queryClient.invalidateQueries({ queryKey: ['avalanche-danger'] }),
      queryClient.invalidateQueries({ queryKey: ['snow-depth-map-data'] }),
    ])
  }

  const userSavedWebcams = userWebcams.data || []
  // Compute saved webcams using the helper function
  const savedWebcams = useMemo(
    () => getSavedWebcams(allWebcams || [], userSavedWebcams),
    [allWebcams, userSavedWebcams],
  )

  return (
    <>
      <div className="min-h-screen flex flex-col bg-background relative">
        <SnowflakeBackground />
        <div className="relative z-10 flex flex-col min-h-screen">
          {/* Header - Always visible on desktop, hidden when map/radar tab active on mobile */}
          <Header
            lastRefresh={lastRefresh}
            onRefresh={handleRefresh}
            isLoading={isRefreshing}
            className={`${activeTab === 'locations' ? 'block' : 'hidden md:block'}`}
          />

          {/* Locations tab content - visible when activeTab is 'locations' on mobile, always visible on desktop */}
          <div
            className={`${activeTab === 'locations' ? 'block' : 'hidden'} md:block flex-1 flex flex-col pb-20 md:pb-0 pt-[72px] md:pt-0`}
          >
            {/* Main Ticker */}
            <div className="border-b border-ice/20">
              <TickerTape />
            </div>

            {/* Stats Summary */}
            <StatsPanel
              searchTriggerRef={searchTriggerRef}
              setNewLocationId={setNewLocationId}
              onAddLocationSuccess={onAddLocationSuccess}
              onMapClick={() => {
                // On mobile: switch to map tab
                // On desktop: this won't be called (StatsPanel will use its own modal)
                setActiveTab('map')
              }}
            />

            {/* Station Grid */}
            <main className="flex-1">
              <StationGrid
                onAddStationClick={handleAddStationClick}
                newLocationId={newLocationId}
                onAddLocationClick={handleAddLocationClick}
              />

              {/* Live Cameras */}
              <WebcamSection
                savedWebcams={savedWebcams}
                allWebcams={allWebcams || []}
                isWebcamSaved={userWebcams.isWebcamSaved}
                onAddWebcam={handleAddWebcam}
                onRemoveWebcam={handleRemoveWebcam}
                isLoading={false}
              />
            </main>

            <Footer />
          </div>

          {/* Map tab content - visible when activeTab is 'map' on mobile only */}
          <div
            className={`${activeTab === 'map' ? 'block' : 'hidden'} md:hidden fixed inset-0 z-40`}
          >
            {activeTab === 'map' && (
              <SnowDepthMapWrapper
                open={true}
                onOpenChange={() => setActiveTab('locations')}
                initialView="regional"
                isActiveTab={true}
              />
            )}
          </div>

          {/* Radar tab content - embedded radar map */}
          <div
            className={`${activeTab === 'radar' ? 'block' : 'hidden'} md:hidden fixed inset-0 z-40`}
          >
            {activeTab === 'radar' && (
              <iframe
                src="https://snowspot-radar-map-three.vercel.app/embed/map"
                className="w-full h-full border-0"
                allow="geolocation"
                title="SnowSpot Radar Map"
              />
            )}
          </div>

          {/* Mobile Bottom Navigation */}
          <MobileBottomNav activeTab={activeTab} onTabChange={setActiveTab} />
        </div>
      </div>
    </>
  )
}
